import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { PersonRoleItem, RbacRole } from '@shared/types/rbac';
import { useMemo, useState } from 'react';
import {
  useAssignPersonRoleMutation,
  useBustRbacCacheMutation,
  useGetAdminPersonRolesQuery,
  useGetAdminRolesQuery,
  useUnassignPersonRoleMutation,
} from '../../store/api/apiSlice';

type AddRoleSelection = Record<string, string>;

export default function AdminUsersPage() {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const per = 25;
  const [busyPersonId, setBusyPersonId] = useState<string | null>(null);
  const [addSelection, setAddSelection] = useState<AddRoleSelection>({});

  const {
    data: roleData,
    isLoading: isLoadingRoles,
    isError: isRoleError,
    refetch: refetchRoles,
  } = useGetAdminRolesQuery();
  const { data, isFetching, isLoading, isError, error, refetch } = useGetAdminPersonRolesQuery({
    q: searchTerm || undefined,
    page,
    per,
  });
  const [assignPersonRole, { isLoading: isAssigning, error: assignError }] =
    useAssignPersonRoleMutation();
  const [unassignPersonRole, { isLoading: isUnassigning, error: unassignError }] =
    useUnassignPersonRoleMutation();
  const [bustRbacCache] = useBustRbacCacheMutation();

  const roles = roleData?.roles ?? [];
  const totalPages = useMemo(() => {
    if (!data?.total) return 1;
    return Math.max(1, Math.ceil(data.total / per));
  }, [data?.total]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearchTerm(searchInput.trim());
  };

  const handleAssign = async (person: PersonRoleItem, roleName: string) => {
    if (!roleName) return;
    setBusyPersonId(person.person_id);
    try {
      await assignPersonRole({
        personId: person.person_id,
        roleName,
        orgId: person.org_id || undefined,
      }).unwrap();
      // Best-effort cache bust - backend already busts cache, this is a secondary safety net
      bustRbacCache({ personId: person.person_id, orgId: person.org_id || undefined }).catch(
        (err) => console.warn('Cache bust failed (non-critical):', err)
      );
      setAddSelection((prev) => ({ ...prev, [person.person_id]: '' }));
    } catch (err) {
      console.error('Failed to assign role', err);
    } finally {
      setBusyPersonId(null);
    }
  };

  const handleUnassign = async (
    person: PersonRoleItem,
    roleName: string,
    orgId?: string | null
  ) => {
    if (!window.confirm(`Remove role "${roleName}" from ${person.email}?`)) return;
    setBusyPersonId(person.person_id);
    try {
      await unassignPersonRole({
        personId: person.person_id,
        roleName,
        orgId: orgId || undefined,
      }).unwrap();
      // Best-effort cache bust - backend already busts cache, this is a secondary safety net
      bustRbacCache({ personId: person.person_id, orgId: person.org_id || undefined }).catch(
        (err) => console.warn('Cache bust failed (non-critical):', err)
      );
    } catch (err) {
      console.error('Failed to unassign role', err);
    } finally {
      setBusyPersonId(null);
    }
  };

  const availableRolesForPerson = (person: PersonRoleItem): RbacRole[] => {
    const existing = new Set(person.roles.map((r) => r.role_name));
    return roles.filter((r) => !existing.has(r.name));
  };

  const isBusy = (personId: string) =>
    busyPersonId === personId || isAssigning || isUnassigning || isFetching;

  return (
    <Box sx={{ maxWidth: 1400, margin: '0 auto', padding: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h4" component="h1">
          User roles
        </Typography>
        {(isFetching || isLoading || isLoadingRoles) && <CircularProgress size={20} />}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Admin-only view of people and their roles. Updates take effect immediately.
      </Typography>

      {(isError || isRoleError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load data. {(error as any)?.data?.error_description || 'Try refreshing.'}
        </Alert>
      )}
      {assignError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to assign role.
        </Alert>
      )}
      {unassignError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(unassignError as any)?.data?.error === 'role_managed_by_sync'
            ? (unassignError as any).data.error_description
            : 'Failed to remove role.'}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          component="form"
          onSubmit={handleSearchSubmit}
          sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}
        >
          <TextField
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by email or user id"
            size="small"
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" />,
            }}
          />
          <Button type="submit" variant="contained" startIcon={<SearchIcon />}>
            Search
          </Button>
          <Button
            type="button"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              refetch();
              refetchRoles();
            }}
          >
            Refresh
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            Page {page} of {totalPages} • {data?.total ?? 0} users
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 600, overflow: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Person</TableCell>
                  <TableCell>Org</TableCell>
                  <TableCell width="45%">Roles</TableCell>
                  <TableCell align="right">Add role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.items || []).map((person) => {
                  const personBusy = isBusy(person.person_id);
                  const available = availableRolesForPerson(person);
                  return (
                    <TableRow key={person.person_id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="subtitle2">{person.email}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {person.user_id || '—'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{person.org_name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {person.org_id || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {person.roles.length === 0 ? (
                            <Chip size="small" label="No roles" color="default" />
                          ) : (
                            person.roles.map((role) => (
                              <Chip
                                key={`${role.role_name}-${role.org_id || 'global'}`}
                                size="small"
                                label={role.role_name}
                                onDelete={
                                  personBusy
                                    ? undefined
                                    : () => handleUnassign(person, role.role_name, role.org_id)
                                }
                                deleteIcon={<DeleteIcon />}
                                disabled={personBusy}
                              />
                            ))
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Select
                            size="small"
                            displayEmpty
                            disabled={personBusy || available.length === 0 || isLoadingRoles}
                            value={addSelection[person.person_id] || ''}
                            onChange={(e) =>
                              setAddSelection((prev) => ({
                                ...prev,
                                [person.person_id]: e.target.value as string,
                              }))
                            }
                            renderValue={(value) => value || 'Select a role'}
                            sx={{ minWidth: 160 }}
                          >
                            {available.length === 0 ? (
                              <MenuItem disabled value="">
                                No available roles
                              </MenuItem>
                            ) : (
                              available.map((role) => (
                                <MenuItem key={role.name} value={role.name}>
                                  {role.name}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={
                              personBusy ||
                              !addSelection[person.person_id] ||
                              isLoadingRoles ||
                              available.length === 0
                            }
                            onClick={() =>
                              handleAssign(person, addSelection[person.person_id] || '')
                            }
                          >
                            Add
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {data?.items?.length === 0 && !isLoading ? (
              <Box sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No users found for this filter.
                </Typography>
              </Box>
            ) : null}
          </TableContainer>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </Button>
          <Button
            variant="outlined"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Showing {data?.items?.length ?? 0} of {data?.total ?? 0}
        </Typography>
      </Box>
    </Box>
  );
}
